import { ArrowLeft, BookOpen, Check, GitCompareArrows, Settings2, ShieldCheck } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { currentStep, referenceDeltas } from '../lib/project'
import type { GeneralReferenceRow, ProjectReferenceChoice, ProjectRow } from '../types'

type Props = {
  project: ProjectRow
  reference: GeneralReferenceRow
  busy: boolean
  onBack: () => void
  onDecide: (choice: ProjectReferenceChoice) => void
}

const choices: Array<{ id: ProjectReferenceChoice; title: string; description: string }> = [
  { id: 'adopt', title: 'Utiliser la nouvelle référence', description: 'Ajouter les changements de la référence tout en conservant les particularités du projet.' },
  { id: 'adapt', title: 'L’adapter pour ce projet', description: 'Utiliser la nouvelle référence puis ouvrir l’éditeur du parcours de ce projet.' },
  { id: 'keep', title: 'Garder la référence actuelle', description: 'Ne pas modifier le parcours et enregistrer cette décision dans l’historique.' },
]

function valueLabel(value: string | null) {
  return value ?? '—'
}

export function ReferenceUpdateView({ project, reference, busy, onBack, onDecide }: Props) {
  const [choice, setChoice] = useState<ProjectReferenceChoice | null>(null)
  const [mobileReadOnly, setMobileReadOnly] = useState(false)
  const deltas = useMemo(() => referenceDeltas(project.snapshot.reference, reference.content), [project.snapshot.reference, reference.content])
  const activeStep = currentStep(project.snapshot)
  const selected = choices.find((item) => item.id === choice)

  useEffect(() => {
    if (!window.matchMedia) return
    const media = window.matchMedia('(max-width: 760px)')
    const update = () => setMobileReadOnly(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  return (
    <main className="workspace reference-update-view">
      <button className="back-link" onClick={onBack} type="button"><ArrowLeft size={17} /> Retour au projet</button>
      <div className="page-heading compact">
        <div><span className="eyebrow">MISE À JOUR DU PARCOURS GÉNÉRAL</span><h1>Une nouvelle référence est disponible</h1><p>{project.name} utilise {project.snapshot.reference.id}. La référence générale est maintenant {reference.content.id}.</p></div>
        <div className="difference-count"><GitCompareArrows size={20} /><strong>{deltas.length}</strong><span>changement{deltas.length > 1 ? 's' : ''}</span></div>
      </div>

      <section className="reconciliation-principle">
        <ShieldCheck size={23} />
        <div><strong>Le projet reste inchangé tant que vous ne décidez pas.</strong><span>Son étape actuelle, ses dates, ses fichiers et ses anciennes présentations seront conservés.</span></div>
      </section>

      <section className="reference-change-list" aria-label="Changements de la référence">
        {deltas.length ? deltas.map((delta) => (
          <article key={delta.id}>
            <span className={`reference-change-kind ${delta.kind}`}>{delta.kind === 'add' ? '+' : delta.kind === 'remove' ? '−' : '↔'}</span>
            <div><strong>{delta.label}</strong><span><s>{valueLabel(delta.before)}</s><b>→</b>{valueLabel(delta.after)}</span></div>
          </article>
        )) : <article><BookOpen size={20} /><div><strong>Nouvelle version de référence</strong><span>Le contenu du parcours est inchangé.</span></div></article>}
      </section>

      <section className="reference-impact-summary">
        <div><span>Étape actuelle conservée</span><strong>{activeStep?.name ?? project.status_label}</strong></div>
        <div><span>Version du projet</span><strong>Une nouvelle version seulement après confirmation</strong></div>
        <div><span>Historique</span><strong>Les versions et documents existants restent inchangés</strong></div>
      </section>

      <section className="reference-choice-section">
        <header><Settings2 size={19} /><div><h2>Que souhaitez-vous faire pour ce projet ?</h2><p>Aucun choix n’est sélectionné à votre place.</p></div></header>
        <div className="mobile-readonly-note">Consultation sur téléphone. Utilisez un ordinateur pour modifier le parcours.</div>
        <div className="reference-choice-grid">
          {choices.map((item) => (
            <button className={choice === item.id ? 'selected' : ''} disabled={mobileReadOnly} onClick={() => setChoice(item.id)} type="button" key={item.id}>
              <span className="decision-check">{choice === item.id ? <Check size={16} /> : null}</span>
              <span><strong>{item.title}</strong><small>{item.description}</small></span>
            </button>
          ))}
        </div>
      </section>

      <footer className="reference-decision-footer">
        <div><strong>{selected?.title ?? 'Choisissez une option pour continuer'}</strong><span>{selected?.description ?? 'Rien ne sera appliqué avant votre confirmation.'}</span></div>
        <button className="secondary-button" onClick={onBack} type="button">Plus tard</button>
        <button className="primary-button" disabled={!choice || busy || mobileReadOnly} onClick={() => choice && onDecide(choice)} type="button">Confirmer ce choix</button>
      </footer>
    </main>
  )
}
